/**
 * AthleteSchedule — read-only schedule view for kids signed in via Athlete Mode.
 * Shows their upcoming bookings; if the parent enabled `book_own_sessions` we
 * also let them browse open slots and book/cancel themselves.
 *
 * Sub-pulls /api/me/athlete-context (sessions left + upcoming) and, when
 * booking is enabled, /api/schedule/slots.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    ArrowLeft,
    Calendar,
    Loader2,
    Clock,
    X as CloseIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function fmtDate(iso) {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    return `${WEEKDAY_LABELS[d.getDay()]} · ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

export default function AthleteSchedule() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [ctx, setCtx] = useState(null);
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !user) navigate("/", { replace: true });
        if (!authLoading && user && !user.athlete_mode_for) {
            navigate("/dashboard", { replace: true });
        }
    }, [authLoading, user, navigate]);

    const refresh = useCallback(async () => {
        if (!user?.athlete_mode_for) return;
        try {
            const r = await axios.get(`${API}/me/athlete-context`, { withCredentials: true });
            setCtx(r.data);
            if (r.data?.permissions?.book_own_sessions) {
                try {
                    const s = await axios.get(`${API}/schedule/slots`, { withCredentials: true });
                    setSlots(s.data || []);
                } catch {
                    setSlots([]);
                }
            }
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    if (authLoading || loading || !ctx) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <Loader2 className="w-7 h-7 animate-spin text-brand-red" />
            </div>
        );
    }

    if (!ctx.permissions?.sessions) {
        return (
            <div className="min-h-screen bg-brand-obsidian text-white pt-32 px-6">
                <div className="max-w-2xl mx-auto border border-zinc-800 rounded-2xl bg-zinc-950/50 p-8 text-center text-sm text-zinc-300">
                    Sessions aren't turned on for your account. Ask your parent to enable Sessions from their dashboard.
                </div>
            </div>
        );
    }

    const upcoming = ctx.upcoming_bookings || [];
    const canBook = !!ctx.permissions?.book_own_sessions;
    // ALWAYS render the real count. No more "Unlimited" lie. Sachin 2026-02-28.
    const remaining = typeof ctx.sessions_remaining === "number" ? Math.max(0, ctx.sessions_remaining) : 0;

    return (
        <div data-testid="athlete-schedule" className="bg-brand-obsidian min-h-screen">
            <section className="pt-32 pb-8 border-b border-zinc-900">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    <button
                        type="button"
                        onClick={() => navigate("/athlete")}
                        className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5"
                    >
                        <ArrowLeft className="w-3 h-3" /> Athlete home
                    </button>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" />
                        Schedule
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-wider text-white">MY SESSIONS</h1>
                    <p className="mt-3 text-sm text-zinc-300">
                        <span data-testid="athlete-sessions-left" className="text-white font-display text-2xl tracking-wide">
                            {remaining}
                        </span>{" "}
                        left
                    </p>
                </div>
            </section>

            <section className="py-10">
                <div className="max-w-5xl mx-auto px-6 md:px-10 space-y-10">
                    <div>
                        <h2 className="font-display text-2xl tracking-wider text-white mb-4">UPCOMING</h2>
                        {upcoming.length === 0 ? (
                            <div className="border border-zinc-800 rounded-xl bg-zinc-950/50 p-6 text-sm text-zinc-300">
                                Nothing on the calendar yet.
                            </div>
                        ) : (
                            <ul className="space-y-2">
                                {upcoming.map((b) => (
                                    <UpcomingRow
                                        key={b.booking_id}
                                        booking={b}
                                        canCancel={canBook}
                                        onCancelled={refresh}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>

                    {canBook && (
                        <div>
                            <h2 className="font-display text-2xl tracking-wider text-white mb-4">BOOK A SLOT</h2>
                            {slots.length === 0 ? (
                                <div className="border border-zinc-800 rounded-xl bg-zinc-950/50 p-6 text-sm text-zinc-300">
                                    No open slots right now. Check back soon.
                                </div>
                            ) : (
                                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {slots.slice(0, 18).map((s) => (
                                        <SlotCard
                                            key={s.slot_id}
                                            slot={s}
                                            athleteId={ctx.athlete_id}
                                            onBooked={refresh}
                                        />
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

function UpcomingRow({ booking, canCancel, onCancelled }) {
    const [busy, setBusy] = useState(false);
    const cancel = async () => {
        // Model B cutoffs: >12h free · 1h-12h late cancel · <1h blocked.
        let prompt = "Cancel this session?";
        try {
            const slotMs = new Date(`${booking.date}T${booking.start_time}:00Z`).getTime();
            const hoursOut = (slotMs - Date.now()) / 3.6e6;
            if (hoursOut < 1) {
                window.alert(
                    "Too late to cancel — sessions can't be cancelled within 1 hour of start.\n" +
                    "Contact your coach if you can't make it; they'll mark it as a no-show."
                );
                return;
            }
            if (hoursOut < 12) {
                prompt = "\u26A0 LATE CANCEL (within 12h of start). 2 late cancels in a calendar month = 1 paid session deducted. Cancel anyway?";
            }
        } catch { /* ignore */ }
        if (!window.confirm(prompt)) return;
        setBusy(true);
        try {
            const r = await axios.delete(`${API}/schedule/bookings/${booking.booking_id}`, { withCredentials: true });
            const msg = r.data?.message || "Cancelled";
            if (r.data?.late_cancel) {
                if (r.data?.penalty?.penalised) {
                    toast.error("Late cancel — 1 session deducted", { description: msg, duration: 8000 });
                } else {
                    toast.warning("Late cancel recorded", { description: msg, duration: 7000 });
                }
            } else {
                toast.success("Cancelled — credit refunded", { description: msg });
            }
            await onCancelled();
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 409) {
                toast.error("Too late to cancel", { description: detail, duration: 9000 });
            } else {
                toast.error("Couldn't cancel", { description: detail });
            }
        } finally {
            setBusy(false);
        }
    };
    return (
        <li
            data-testid={`athlete-upcoming-row-${booking.booking_id}`}
            className="flex items-center justify-between gap-3 border border-zinc-800 bg-zinc-950/50 rounded-xl px-4 py-3"
        >
            <div className="flex items-center gap-3 text-sm text-zinc-200">
                <Calendar className="w-4 h-4 text-brand-red" />
                <div>
                    <div className="text-white">{fmtDate(booking.date)} · {booking.start_time}</div>
                    <div className="text-xs text-zinc-400 mt-0.5">
                        {booking.label || "Small Group"} · {booking.duration_minutes} min
                    </div>
                </div>
            </div>
            {canCancel && (
                <button
                    type="button"
                    data-testid={`athlete-cancel-${booking.booking_id}`}
                    onClick={cancel}
                    disabled={busy}
                    className="text-zinc-300 hover:text-brand-red inline-flex items-center gap-1 text-xs"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CloseIcon className="w-3 h-3" />} Cancel
                </button>
            )}
        </li>
    );
}

function SlotCard({ slot, athleteId, onBooked }) {
    const [busy, setBusy] = useState(false);
    const book = async () => {
        setBusy(true);
        try {
            await axios.post(
                `${API}/schedule/bookings`,
                { slot_id: slot.slot_id, athlete_id: athleteId },
                { withCredentials: true },
            );
            toast.success("Booked!");
            await onBooked();
        } catch (e) {
            toast.error("Couldn't book", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };
    return (
        <li className="border border-zinc-800 bg-zinc-950/50 rounded-xl p-4">
            <div className="flex items-center gap-2 text-sm text-white">
                <Clock className="w-3.5 h-3.5 text-brand-navy-300" />
                {fmtDate(slot.date)} · {slot.start_time}
            </div>
            <div className="text-xs text-zinc-400 mt-1">
                {slot.label || "Small Group"} · {slot.duration_minutes} min
            </div>
            <button
                type="button"
                data-testid={`athlete-book-${slot.slot_id}`}
                onClick={book}
                disabled={busy || !slot.available}
                className="mt-3 w-full btn-tactical text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {slot.available ? "Book" : "Full"}
            </button>
        </li>
    );
}
